import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "services/api.service";
import { defaultPickup, Pickup } from "types/pickup";
import { incompleteDeviceFilterSearch } from "./incompleteDeviceFilter";
import { useAlert } from "./useAlert";

export default function usePickup() {
  const params = useParams();
  const location = useLocation();

  const [pickup, setPickup] = useState<Pickup>(defaultPickup);
  const { alertSuccess, alertError, alertInfo } = useAlert();
  const navigate = useNavigate();

  const getPickup = useCallback(() => {
    api.get(`/pickups/${params.pickupId || params.pickupUuid}`).then(({ data }) => {
      setPickup(data);
    });
  }, [params.pickupId, params.pickupUuid]);

  const updatePickup = (props: Pickup) => {
    const { softthinksMode, ewasteInLbs } = props;
    return api
      .put(`/pickups/${params.pickupId || props.uuid}`, {
        pickup: {
          softthinksMode,
          ewasteInLbs,
        },
      })
      .then(() => {
        alertSuccess(`Successfully updated the pickup`);
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const createPickup = (newPickup: Pickup) => {
    return api
      .post("/pickups", { newPickup })
      .then(({ data }) => {
        navigate(`/logistics/pickups/${data.uuid}/edit`);
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const irReady = (pickupUuid: string) => {
    return api
      .put(`/pickups/${pickupUuid}/ir_ready`)
      .then(() => {
        getPickup();
        alertSuccess(`Successfully marked as IR Ready`);
      })
      .catch(({ response }) => {
        const error = response?.data?.errors ?? {};
        const incompleteDevices = Array.isArray(error.incompleteDevices) ? error.incompleteDevices : [];
        const onPickupInventoryGrid = /^\/pickups\/[^/]+$/.test(location.pathname);
        if (error.state && incompleteDevices.length > 0 && onPickupInventoryGrid) {
          const deviceIds = incompleteDevices.map((device: { id: number }) => device.id);
          alertError(`${error.state[0]}\n\nApply a filter to see these devices?`, {
            label: "Apply filter",
            onClick: () => navigate(incompleteDeviceFilterSearch(deviceIds)),
          });
        } else {
          const messages = Object.values(error)
            .flat()
            .filter((message): message is string => typeof message === "string");
          alertError(messages.length ? messages.join("\n") : "There was an error, please try again");
        }
      });
  };

  const frReady = (pickupUuid: string) => {
    return api
      .put(`/pickups/${pickupUuid}/fr_ready`)
      .then(() => {
        getPickup();
        alertSuccess(`Successfully marked as FR Ready`);
      })
      .catch(({ response }) => {
        const errors = response?.data?.errors ?? {};
        const messages = Object.values(errors)
          .flat()
          .filter((message): message is string => typeof message === "string");
        alertError(messages.length ? messages.join("\n") : "There was an error, please try again");
      });
  };

  const forceFrReady = (pickupUuid: string) => {
    return api
      .put(`/pickups/${pickupUuid}/force_fr_ready`)
      .then(() => {
        getPickup();
        alertSuccess(`Successfully marked as FR Ready`);
      })
      .catch(() => {
        alertError("There was an error, please try again");
      });
  };

  const enrollmentCheck = (pickupUuid: string) => {
    return api
      .post(`/pickups/${pickupUuid}/enrollment_check`)
      .then(() =>
        alertInfo(
          "Successfully enqueued enrollment checks for Mac devices missing enrollment status. This may take some time to complete.",
        ),
      )
      .catch(() => alertError("Couldn't perform enrollment check action."));
  };

  const fetchPickupByShippingLabel = (shippingLabel: string) => {
    return api.get(`/pickups/virtual_pickup?tracking_number=${shippingLabel}`);
  };

  useEffect(() => {
    if (params.pickupId || params.pickupUuid) {
      getPickup();
    }
  }, [getPickup, params.pickupId, params.pickupUuid]);

  return {
    irReady,
    frReady,
    forceFrReady,
    pickup,
    updatePickup,
    createPickup,
    fetchPickupByShippingLabel,
    enrollmentCheck,
  };
}
